import { type AuthInfos, authInfosDefaults, UserAccess } from '../models/auth-infos'
import { OTPService, type OTPCode } from './otp.service'
import type { HttpAdapter } from '../../core/adapters/axios.adapter'
import type { AnalyticsService } from '../../core/services/analytics.service'

/**
 * Custom error thrown when an email address is not found.
 */

export class AuthService {
  readonly TBWJF = 'tbw-auth-infos'
  private _authInfos: AuthInfos = authInfosDefaults
  private analyticsService?: AnalyticsService
  constructor(private _httpAdapter: HttpAdapter, private _otpService: OTPService) { }

  bindAnalytics(analyticsService: AnalyticsService) {
    this.analyticsService = analyticsService
  }

  get isAuthenticated() {
    return this._authInfos.userAccess !== UserAccess.Visitor
  }

  get accountType() {
    if (!this.authInfos) { return null }
    return this.authInfos.userAccess
  }

  get authInfos(): AuthInfos {
    return this._authInfos
  }

  get isFreemium(): boolean {
    const freemium = this.authInfos.userAccess === UserAccess.Free
    return this.isAuthenticated && freemium
  }

  get isPremium(): boolean {
    return this.isAuthenticated && (this.authInfos.userAccess === UserAccess.Premium || this.authInfos.userAccess === UserAccess.Founder)
  }

  get isVisitor(): boolean {
    return !this.isAuthenticated
  }

  notify(authenticated: boolean) {
    const customEvent = new CustomEvent('authChange', {
      detail: { authenticated },
      bubbles: true,
      cancelable: true
    });

    document.dispatchEvent(customEvent);
  }

  async sendEmail(email: string) {
    const response = await this._httpAdapter.post('query', { email });
    return response.data
  }

  register(token: string) {
    localStorage.setItem(this.TBWJF, token)
  }

  logout() {
    if (this.isAuthenticated) {
      this.analyticsService?.track('logout')
      this.analyticsService?.clearUserContext()
    }
    this.clear()
  }

  async verifyOTP(email: string, otpCode: OTPCode) {
    const res = await this._otpService.verifyOTP(email, otpCode)
    if (res) {
      this.register(res.token)
      await this.setAuthInfos(res.token)
      this.analyticsService?.setUserContext()
      this.analyticsService?.track('login')
    }
    return res
  }

  async setAuthInfos(token: string) {
    const response = await this._httpAdapter.get<AuthInfos>('', { headers: { Authorization: `Bearer ${token}` } });
    this._authInfos = response.data as AuthInfos;
  }

  private clear() {
    this._authInfos = authInfosDefaults;
    localStorage.removeItem(this.TBWJF);
    this.notify(false);
  }

  private getFromStorage(): string | null {
    return localStorage.getItem(this.TBWJF);
  }

  async checkAuth() {
    const token = this.getFromStorage();

    if (!token) {
      this.notify(false);
      return false
    }

    try {
      this.notify(true);
      await this.setAuthInfos(token)
      this.analyticsService?.setUserContext()
      this.analyticsService?.track('custom_page_view')
      return true;
    } catch (response) {
      console.error('auth check failed', response)
      this.clear();
      return false
    }

  }
}
